import { createSelectorMemoized } from '@base-ui/utils/store';
import { EMPTY_ARRAY } from '@base-ui/utils/empty';
import type { SchedulerEventId, SchedulerResourceId } from '@mui/x-scheduler-internals/models';
import type { SchedulerState } from '@mui/x-scheduler-internals/internals';
import type {
  SchedulerDependenciesState,
  SchedulerDependencyId,
  SchedulerResolvedDependencyLag,
  SchedulerDependencyType,
} from '../models';
import type { EventTimelinePremiumState as State } from '../use-event-timeline-premium';
import {
  getActiveDependencies,
  getEffectiveDependencyLag,
  groupByEventId,
} from '../internals/utils/dependency-utils';

// Typed against the two slices they read, so the scheduling plugin (generic over
// `SchedulerState & SchedulerDependenciesState`) shares them with the rendering.
type DependenciesState = SchedulerState & SchedulerDependenciesState;

const activeModelListSelector = createSelectorMemoized(
  (state: DependenciesState) => state.dependencyModelLookup,
  (state: DependenciesState) => state.processedEventLookup,
  getActiveDependencies,
);

export interface SchedulerDependencySourceDescription {
  title: string;
  type: SchedulerDependencyType;
  /**
   * The lag the engine applies to this successor, or `null` when the dependency has no
   * usable lag.
   */
  lag: SchedulerResolvedDependencyLag | null;
}

const activeSourcesByTargetSelector = createSelectorMemoized(
  activeModelListSelector,
  (state: State) => state.processedEventLookup,
  (dependencies, processedEventLookup) => {
    const sourcesByTarget = new Map<SchedulerEventId, SchedulerDependencySourceDescription[]>();
    for (const dependency of dependencies) {
      // Active dependencies always resolve: their events exist in the lookup.
      const source = {
        title: processedEventLookup.get(dependency.source)!.title,
        type: dependency.type,
        lag: getEffectiveDependencyLag(
          dependency,
          processedEventLookup.get(dependency.target)!.allDay,
        ),
      };
      const sources = sourcesByTarget.get(dependency.target);
      if (sources) {
        sources.push(source);
      } else {
        sourcesByTarget.set(dependency.target, [source]);
      }
    }
    return sourcesByTarget;
  },
);

const selectedIdSelector = (state: State) => {
  const selection = state.selection;
  return selection?.type === 'dependency' && state.dependencyModelLookup.has(selection.id)
    ? selection.id
    : null;
};

const creationSelector = (state: State) => state.dependencyCreation;

export const eventTimelinePremiumDependencySelectors = {
  modelList: (state: State) => state.dependencyModelList,
  modelLookup: (state: State) => state.dependencyModelLookup,
  model: (state: State, dependencyId: SchedulerDependencyId) =>
    state.dependencyModelLookup.get(dependencyId) ?? null,
  /**
   * Dependencies with a supported type whose two events exist and are not recurring.
   * Rendering and the scheduling engine must only consume these.
   */
  activeModelList: activeModelListSelector,
  activeModelListBySource: createSelectorMemoized(activeModelListSelector, (dependencies) =>
    groupByEventId(dependencies, 'source'),
  ),
  activeModelListByTarget: createSelectorMemoized(activeModelListSelector, (dependencies) =>
    groupByEventId(dependencies, 'target'),
  ),
  /**
   * The source event title, type and lag of the active dependencies, grouped by target
   * event id. Used to describe an event with the events it depends on.
   */
  activeSourcesByTarget: activeSourcesByTargetSelector,
  activeSourcesForTarget: (
    state: State,
    eventId: SchedulerEventId,
  ): readonly SchedulerDependencySourceDescription[] =>
    activeSourcesByTargetSelector(state).get(eventId) ?? EMPTY_ARRAY,
  /**
   * Whether the dependencies feature is enabled (internal parameters provided).
   */
  enabled: (state: State) => state.areDependenciesEnabled,
  /**
   * The pending create-dependency drag gesture, or `null`.
   */
  creation: creationSelector,
  // Keyed by occurrence *and* resource: an event appearing on several resources
  // repeats the same occurrence key on each row, and only the row appearance the
  // gesture actually involves must highlight.
  isCreationTarget: (state: State, occurrenceKey: string, resourceId: SchedulerResourceId) => {
    const creation = creationSelector(state);
    return (
      creation !== null &&
      creation.targetOccurrenceKey === occurrenceKey &&
      creation.targetResourceId === resourceId
    );
  },
  /**
   * The id of the selected dependency, or `null`.
   * The masking is membership-only: an id absent from the dependency lookup resolves
   * to `null` for the same render (the store effect then clears the raw value), but a
   * dependency deactivated by a recurring or unknown endpoint is still in the lookup
   * and resolves normally.
   */
  selectedId: selectedIdSelector,
  /**
   * The selected dependency, or `null`.
   */
  selectedModel: (state: State) => {
    const selectedId = selectedIdSelector(state);
    return selectedId === null ? null : (state.dependencyModelLookup.get(selectedId) ?? null);
  },
  /**
   * The dependency open in the dependency dialog and its anchor, or `null`.
   * Masked by membership like `selectedId`.
   */
  editor: (state: State) => {
    const editor = state.dependencyEditor;
    return editor !== null && state.dependencyModelLookup.has(editor.dependencyId) ? editor : null;
  },
  /**
   * Whether dependencies cannot be created, edited or deleted: only the scheduler-wide
   * `readOnly` locks them. A read-only event is protected by the cascade veto instead.
   */
  isReadOnly: (state: DependenciesState) => state.readOnly,
};
